import { BaseInputDevice, Debouncer } from './InputDevice.ts';

/**
 * WebSerial adapter for a microcontroller reporting a switch and/or an EMG
 * envelope over USB CDC.
 *
 * ## Wire protocol
 *
 * Newline-delimited ASCII, one record per line, 115200 8N1 by default:
 *
 *   B1            button pressed
 *   B0            button released
 *   E <int>       EMG sample on channel 0
 *   E <ch> <int>  EMG sample on channel <ch>
 *   #<text>       comment / banner, ignored
 *
 * That is all. The protocol is deliberately trivial so it can be implemented on
 * an Arduino in twenty lines, and so a line that fails to parse can be dropped
 * without desynchronising the stream.
 *
 * ## Timestamps
 *
 * Timestamps are taken with `performance.now()` at the moment the line is
 * parsed in the browser. That includes USB transfer and the browser's read
 * scheduling -- typically a few milliseconds, but it is real latency and it
 * belongs in the measurement. If the device can timestamp its own edges,
 * `input -> flap` in the latency panel is where the discrepancy will show up.
 *
 * ## Availability
 *
 * WebSerial requires a Chromium-based browser, a secure context, and an
 * explicit user gesture to call `requestPort()`. There is no way around the
 * gesture, so `connect()` must be wired to a button, not to page load.
 */
export interface SerialInputOptions {
  baudRate: number;
  /** Contact debounce window, ms. */
  debounceMs: number;
  /** Reuse an already-granted port instead of prompting. */
  port?: SerialPort;
}

export const DEFAULT_SERIAL_OPTIONS: SerialInputOptions = {
  baudRate: 115200,
  debounceMs: 25,
};

export class SerialInput extends BaseInputDevice {
  readonly id = 'serial-input';
  readonly label = 'Serial button / EMG (WebSerial)';
  override readonly providesEmg = true;

  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private buffer = '';
  private debouncer: Debouncer;
  private lineCount = 0;
  private badLineCount = 0;
  private stopping = false;

  readonly options: SerialInputOptions;

  constructor(options: Partial<SerialInputOptions> = {}) {
    super();
    this.options = { ...DEFAULT_SERIAL_OPTIONS, ...options };
    this.debouncer = new Debouncer(this.options.debounceMs);
  }

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'serial' in navigator;
  }

  async connect(): Promise<void> {
    if (!SerialInput.isSupported()) {
      this.status = 'error';
      this.lastError = 'WebSerial is not available in this browser';
      throw new Error(this.lastError);
    }
    this.status = 'connecting';
    this.stopping = false;
    try {
      this.port = this.options.port ?? (await navigator.serial.requestPort());
      await this.port.open({ baudRate: this.options.baudRate });
      this.status = 'connected';
      this.lastError = null;
      void this.readLoop();
    } catch (err) {
      this.status = 'error';
      this.lastError = String(err);
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    this.stopping = true;
    try {
      await this.reader?.cancel();
    } catch {
      // Cancelling a reader that is already closed is not an error worth surfacing.
    }
    this.reader = null;
    try {
      await this.port?.close();
    } catch {
      // Same.
    }
    this.port = null;
    this.buffer = '';
    this.debouncer.reset();
    this.status = 'disconnected';
  }

  override info(): Record<string, unknown> {
    return {
      ...super.info(),
      baudRate: this.options.baudRate,
      lines: this.lineCount,
      badLines: this.badLineCount,
    };
  }

  private async readLoop(): Promise<void> {
    if (!this.port?.readable) return;
    const decoder = new TextDecoder();
    const reader = this.port.readable.getReader();
    this.reader = reader;
    try {
      while (!this.stopping) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        this.buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = this.buffer.indexOf('\n')) >= 0) {
          const line = this.buffer.slice(0, nl).trim();
          this.buffer = this.buffer.slice(nl + 1);
          if (line) this.parseLine(line, performance.now());
        }
        // A device that never sends newlines must not grow the buffer forever.
        if (this.buffer.length > 4096) this.buffer = '';
      }
    } catch (err) {
      if (!this.stopping) {
        this.status = 'error';
        this.lastError = String(err);
      }
    } finally {
      reader.releaseLock();
      this.reader = null;
    }
  }

  /** Exposed for unit tests; also the whole of the protocol implementation. */
  parseLine(line: string, timestamp: number): void {
    this.lineCount++;
    if (line.startsWith('#')) return;

    if (line === 'B1' || line === 'B0') {
      const down = line === 'B1';
      if (this.debouncer.accept(down, timestamp)) {
        this.emitButton({ down, timestamp, source: this.id });
      }
      return;
    }

    if (line[0] === 'E') {
      const parts = line.split(/\s+/);
      if (parts.length === 2) {
        const value = Number(parts[1]);
        if (Number.isFinite(value)) {
          this.emitEmg({ value, timestamp, channel: 0, source: this.id });
          return;
        }
      } else if (parts.length === 3) {
        const channel = Number(parts[1]);
        const value = Number(parts[2]);
        if (Number.isFinite(channel) && Number.isFinite(value)) {
          this.emitEmg({ value, timestamp, channel, source: this.id });
          return;
        }
      }
    }

    this.badLineCount++;
  }
}
